import { Command, CommandInput, CommandList, CommandItem, CommandGroup, CommandEmpty } from '@/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useState, useRef, useEffect } from 'react';
import type { MakeModelItem, MakeModel } from '@tac/vehicle-api-client/types';
import { ArrowRight } from '@tac/icons';

type MakeModelCommandMenuProps = {
  value?: { make: MakeModelItem; model: MakeModelItem } | null;
  onValueChange?: (value: { make: MakeModelItem; model: MakeModelItem } | null) => void;
  disabled?: boolean;
  allMakesModels: MakeModel[];
};

export const MakeModelCommandMenu = ({
  value = null,
  onValueChange,
  disabled = false,
  allMakesModels,
}: MakeModelCommandMenuProps) => {
  const [isResultsListOpen, setIsResultsListOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ? `${value.make.description} ${value.model.description}` : '');
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const commandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value ? `${value.make.description} ${value.model.description}` : '');
  }, [value]);

  const handleInputChange = (newInputValue: string) => {
    setTouched(false);
    setInputValue(newInputValue);

    if (newInputValue.length > 0) {
      setIsResultsListOpen(true);
    } else {
      setIsResultsListOpen(false);
      onValueChange?.(null);
    }
  };

  const handleSelectionChange = (make: MakeModelItem, model: MakeModelItem) => {
    const displayValue = `${make.description} ${model.description}`;
    setTouched(true);
    setInputValue(displayValue);
    onValueChange?.({ make, model });
    setIsResultsListOpen(false);
  };

  return (
    <>
      {/* Hidden form inputs for make and model IDs since CommandInput only handles one input */}
      <input type="hidden" name="makeid" value={value?.make.id ?? ''} />
      <input type="hidden" name="modelid" value={value?.model.id ?? ''} />

      <Command ref={commandRef} label="Enter make or model">
        <Popover open={isResultsListOpen} onOpenChange={setIsResultsListOpen}>
          <PopoverAnchor>
            <PopoverTrigger asChild>
              <CommandInput
                ref={inputRef}
                placeholder="Enter make or model"
                value={inputValue}
                onValueChange={handleInputChange}
                disabled={disabled}
                touched={touched}
              />
            </PopoverTrigger>
          </PopoverAnchor>
          <PopoverContent onOpenAutoFocus={(e) => e.preventDefault()} className="!w-[var(--radix-popper-anchor-width)]">
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {allMakesModels.flatMap(({ make, models }) =>
                  models.map((model) => {
                    const itemValue = `${make.description} ${model.description}`;
                    return (
                      <CommandItem
                        className="flex justify-between"
                        key={`${make.id}-${model.id}`}
                        value={itemValue}
                        onSelect={() => handleSelectionChange(make, model)}
                      >
                        {itemValue}
                        <ArrowRight />
                      </CommandItem>
                    );
                  })
                )}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    </>
  );
};
